import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Select,
  Badge,
  DataTable,
  ConfirmDialog,
  Alert,
  Skeleton,
  ErrorBoundary,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { locationService } from '@/services/location.service';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import type { Location } from '@/types/location';

// ─── Design tokens ─────────────���────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

// Filter bar must sit above the DataTable below it so the open Status filter
// dropdown overlays the table cleanly. The glass panel's backdrop-blur creates
// its own stacking context, which traps the Select's z-50 inside the panel;
// without an explicit z-index the opaque table (and its sticky header at z-10)
// paints over the overflowing dropdown. `relative z-20` lifts the panel above
// the table's sticky header. Regression: GitHub issue #132.
export const FILTER_BAR_CLASS = cn(GLASS_PANEL, 'relative z-20 p-4 mb-6');

// ─── Filter options (spec 3.10) ─��───────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ─── Table column defs (spec: Name, Code, Address, Users, Status, Today's Sales)
type LocationRow = Location & { id: string };

const columns: ColumnDef<LocationRow>[] = [
  {
    key: 'name',
    header: 'Location',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</p>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{row.location_code}</p>
      </div>
    ),
  },
  {
    key: 'address',
    header: 'Address',
    render: (_v, row) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-300">
        {[row.city, row.state].filter(Boolean).join(', ') || '—'}
      </span>
    ),
  },
  {
    key: 'user_count',
    header: 'Users',
    numeric: true,
    render: (_v, row) => (
      <span className="tabular-nums text-sm">{row.user_count ?? 0}</span>
    ),
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (_v, row) => (
      <Badge variant={row.is_active ? 'success' : 'danger'} size="sm">
        {row.is_active ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: 'gstin',
    header: 'GSTIN',
    render: (_v, row) => (
      <span className="text-sm text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
        {row.gstin || '—'}
      </span>
    ),
  },
];

// ─── Row actions ─────────────────────────────────────────────────────────────
// Deactivation is a reversible soft-delete (is_active flag), so the inactive
// state must expose an Activate action that mirrors Deactivate — otherwise a
// location can never be re-enabled from the list. Regression: GitHub issue #133.
export type LocationRowAction = 'view' | 'activate' | 'deactivate';

export function getLocationRowActions(isActive: boolean): LocationRowAction[] {
  return isActive ? ['view', 'deactivate'] : ['view', 'activate'];
}

// ─── Icons ──────────────────────────────────────────────────────────────────
function MapPinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// ─── Content ───────────────────────────────────────���────────────────────────

function LocationListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tax = useTaxRegime();
  const searchRef = useRef<HTMLInputElement>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const statusFilter = searchParams.get('is_active') ?? '';
  const searchQuery = searchParams.get('search') ?? '';

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [deactivateTarget, setDeactivateTarget] = useState<Location | null>(null);

  // Keyboard shortcuts (spec 3.10)
  useKeyboardShortcut('n', useCallback(() => {
    navigate('/locations/new');
  }, [navigate]));

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (searchInput) params.set('search', searchInput);
      else params.delete('search');
      params.set('page', '1');
      setSearchParams(params);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParams, setSearchParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['locations', page, statusFilter, searchQuery],
    queryFn: () =>
      locationService.list({
        page,
        limit: 20,
        search: searchQuery || undefined,
        is_active: statusFilter || undefined,
      }),
    staleTime: 15_000,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => locationService.update(id, { is_active: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setDeactivateTarget(null);
      toast({ title: 'Location deactivated', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => locationService.update(id, { is_active: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      toast({ title: 'Location activated', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  function setFilter(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  }

  const locations: LocationRow[] = (data?.data ?? []) as LocationRow[];

  if (error) {
    return (
      <Alert
        variant="danger"
        title="Failed to load locations"
        description="An error occurred loading the location list. Please try refreshing."
        action={{ label: 'Retry', onClick: () => queryClient.invalidateQueries({ queryKey: ['locations'] }) }}
      />
    );
  }

  const hasNoData = !isLoading && locations.length === 0 && !searchQuery && !statusFilter;
  const totalPages = data?.meta ? Math.ceil(data.meta.total / data.meta.limit) : 1;

  // Action column with inline actions
  const actionColumn: ColumnDef<LocationRow> = {
    key: 'id',
    header: '',
    render: (_v, row) => {
      const actions = getLocationRowActions(row.is_active);
      return (
        <div className="flex gap-1.5 justify-end">
          {actions.includes('view') && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/locations/${row.id}`)}>
              View
            </Button>
          )}
          {actions.includes('deactivate') && (
            <Button variant="ghost" size="sm" onClick={() => setDeactivateTarget(row)}>
              Deactivate
            </Button>
          )}
          {actions.includes('activate') && (
            <Button
              variant="ghost"
              size="sm"
              disabled={reactivateMutation.isPending}
              onClick={() => reactivateMutation.mutate(row.id)}
            >
              Activate
            </Button>
          )}
        </div>
      );
    },
  };

  return (
    <div className="animate-fade-in">
      {/* Filter Bar — glass panel */}
      <div className={FILTER_BAR_CLASS}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-full sm:w-72">
            <Input
              ref={searchRef}
              variant="search"
              placeholder="Search by name, code, city..."
              value={searchInput}
              onChange={setSearchInput}
            />
          </div>
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setFilter('is_active', Array.isArray(v) ? v[0] : v)}
            placeholder="Status"
            className="w-44"
          />
          {(statusFilter || searchQuery) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Empty state — spec: icon=map-pin */}
      {hasNoData ? (
        <div className="animate-slide-up">
          <EmptyState
            variant="page"
            title="No locations yet"
            description="Add your first store location to start managing your business"
            action={{ label: 'Add Location', onClick: () => navigate('/locations/new') }}
            icon={
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500/20 to-indigo-500/20">
                <MapPinIcon />
              </div>
            }
          />
        </div>
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : (
        <div className="animate-slide-up">
          <DataTable<LocationRow>
            columns={[
              ...columns.map((c) =>
                c.key === 'gstin' ? { ...c, header: tax.taxIdLabel ?? 'Tax ID' } : c,
              ),
              actionColumn,
            ]}
            data={locations}
            loading={isLoading}
            variant="default"
            emptyState={{ title: 'No locations found', description: 'Try adjusting your search or filters' }}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              <span className="tabular-nums">
                Page {page} of {totalPages} ({data?.meta.total ?? 0} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(page - 1));
                    setSearchParams(params);
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(page + 1));
                    setSearchParams(params);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Deactivate Confirm */}
      {deactivateTarget && (
        <ConfirmDialog
          open
          title="Deactivate Location"
          description={`Deactivating "${deactivateTarget.name}" will prevent billing at this location. Staff will be unassigned. Continue?`}
          confirmLabel="Deactivate"
          variant="danger"
          loading={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate(deactivateTarget.id)}
          onCancel={() => setDeactivateTarget(null)}
        />
      )}
    </div>
  );
}

export function LocationListPage() {
  const navigate = useNavigate();

  return (
    <PageContainer
      title="Locations"
      subtitle="Manage store locations across your business"
      breadcrumbs={[{ label: 'Locations' }]}
      actions={
        <Button variant="primary" onClick={() => navigate('/locations/new')}>
          + Add Location
        </Button>
      }
    >
      <ErrorBoundary>
        <LocationListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
